import React, { useEffect, useState } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { WeeklyEmailPreview } from './WeeklyEmailPreview';
import {
  Loader2, CalendarCheck, Users, Trophy, Eye, LogIn, MessageCircle, ChevronDown, FileDown, ExternalLink, Heart, Zap,
} from 'lucide-react';

const TYPE_META = {
  login: { icon: LogIn, label: 'Logged in', color: 'text-sky-600 bg-sky-50' },
  page_view: { icon: Eye, label: 'Visited', color: 'text-stone-600 bg-stone-100' },
  download: { icon: FileDown, label: 'Downloaded', color: 'text-[#B8871E] bg-[#EFB342]/15' },
  link_click: { icon: ExternalLink, label: 'Opened', color: 'text-[#B8871E] bg-[#EFB342]/10' },
  checkin: { icon: Zap, label: 'Points', color: 'text-emerald-700 bg-emerald-50' },
  post: { icon: MessageCircle, label: 'Community', color: 'text-rose-600 bg-rose-50' },
  comment: { icon: MessageCircle, label: 'Community', color: 'text-rose-600 bg-rose-50' },
  like: { icon: Heart, label: 'Community', color: 'text-rose-600 bg-rose-50' },
  fist_bump: { icon: Heart, label: 'Leaderboard', color: 'text-rose-600 bg-rose-50' },
  tip_reaction: { icon: Zap, label: 'Coaching', color: 'text-orange-600 bg-orange-50' },
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-CA', {
  hour: 'numeric', minute: '2-digit', timeZone: 'America/Vancouver',
});

const daysBetween = (start, end) => {
  const out = [];
  for (let d = new Date(start + 'T12:00:00'); d <= new Date(end + 'T12:00:00'); d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out.reverse();
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-[#EFB342]/15 text-[#B8871E] flex items-center justify-center"><Icon size={18} /></div>
    <div>
      <p className="text-[11px] uppercase tracking-wide text-stone-500">{label}</p>
      <p className="text-xl font-semibold text-stone-900" data-testid={testId}>{value}</p>
    </div>
  </div>
);

const DayPicker = ({ days, selected, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 mb-5" data-testid="activity-day-picker">
    {days.map((d) => {
      const dt = new Date(d + 'T12:00:00');
      const active = d === selected;
      return (
        <button
          key={d}
          type="button"
          onClick={() => onSelect(d)}
          data-testid={`activity-day-${d}`}
          className={`shrink-0 rounded-xl border px-3 py-2 text-center transition-colors ${
            active ? 'bg-[#EFB342] border-[#EFB342] text-black' : 'bg-white border-stone-200 text-stone-700 hover:border-[#EFB342]/60'
          }`}
        >
          <span className="block text-[10px] uppercase tracking-wide">{dt.toLocaleDateString('en-CA', { weekday: 'short' })}</span>
          <span className="block text-lg font-semibold leading-tight">{dt.getDate()}</span>
        </button>
      );
    })}
  </div>
);

const Chips = ({ items, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {items.length === 0 && <span className="text-stone-400 text-xs">—</span>}
    {items.map(({ label, count }) => (
      <span key={label} className="text-xs bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">
        {label}{count > 1 ? ` ×${count}` : ''}
      </span>
    ))}
  </div>
);

const toItems = (obj) => Object.entries(obj).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);

const Timeline = ({ member }) => (
  <div className="px-4 pb-4 pt-1 grid md:grid-cols-[1fr_2fr] gap-4" data-testid={`activity-timeline-${member.id}`}>
    <div>
      <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Actions logged today</p>
      {member.actions_logged.length ? (
        <ul className="space-y-1">{member.actions_logged.map((a) => <li key={a} className="text-sm text-stone-700">• {a}</li>)}</ul>
      ) : <p className="text-sm text-stone-400">No points logged for this day</p>}
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Timeline</p>
      <ol className="space-y-1.5 max-h-72 overflow-y-auto pr-2">
        {member.timeline.map((t, i) => {
          const meta = TYPE_META[t.type] || TYPE_META.page_view;
          const Icon = meta.icon;
          return (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className="text-xs text-stone-500 w-16 shrink-0">{fmtTime(t.at)}</span>
              <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${meta.color}`}><Icon size={13} /></span>
              <span className="text-stone-700">{t.type === 'page_view' ? `Visited ${t.label}` : t.type === 'download' ? `Downloaded ${t.label}` : t.type === 'link_click' ? `Opened ${t.label}` : t.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  </div>
);

const MemberRow = ({ member }) => {
  const [open, setOpen] = useState(false);
  const c = member.counts;
  const community = (c.post || 0) + (c.comment || 0) + (c.like || 0) + (c.fist_bump || 0);
  return (
    <div className="border-b border-stone-200 last:border-b-0" data-testid={`activity-member-${member.id}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 grid grid-cols-[1.4fr_0.5fr_2fr_0.7fr_0.6fr_24px] gap-3 items-center hover:bg-stone-50 transition-colors"
        data-testid={`activity-member-toggle-${member.id}`}
      >
        <div className="min-w-0">
          <p className="font-medium text-stone-900 truncate">
            {member.name}
            {member.is_trial && <span className="ml-2 text-[10px] text-sky-500 uppercase tracking-wide">Trial</span>}
          </p>
          <p className="text-[11px] text-stone-500 truncate">{member.email}</p>
        </div>
        <span className={`font-semibold ${member.points ? 'text-[#B8871E]' : 'text-stone-400'}`} data-testid={`activity-member-points-${member.id}`}>{member.points} pts</span>
        <div className="space-y-1">
          <Chips items={toItems(member.pages)} testId={`activity-member-pages-${member.id}`} />
          {Object.keys(member.resources).length > 0 && <Chips items={toItems(member.resources)} testId={`activity-member-resources-${member.id}`} />}
        </div>
        <span className="text-sm text-stone-600">{community ? `${community} interaction${community > 1 ? 's' : ''}` : '—'}</span>
        <span className="text-xs text-stone-500">{member.last_active ? fmtTime(member.last_active) : '—'}</span>
        <ChevronDown size={16} className={`text-stone-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <Timeline member={member} />}
    </div>
  );
};

const MemberTable = ({ members }) => (
  <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto" data-testid="activity-member-table">
    <div className="min-w-[860px]">
      <div className="px-4 py-2 grid grid-cols-[1.4fr_0.5fr_2fr_0.7fr_0.6fr_24px] gap-3 text-[11px] uppercase tracking-wide text-stone-500 bg-stone-50 border-b border-stone-200">
        <span>Member</span><span>Points</span><span>Pages &amp; resources</span><span>Community</span><span>Last active</span><span />
      </div>
      {members.length === 0 && <p className="text-center py-10 text-stone-500" data-testid="activity-empty">No member activity recorded on this day.</p>}
      {members.map((m) => <MemberRow key={m.id} member={m} />)}
    </div>
  </div>
);

export default function AdminActivity() {
  const [date, setDate] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get('/admin/activity/daily', { params: date ? { date } : {} })
      .then((res) => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Could not load activity'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const s = data?.summary;
  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F4F1] text-stone-900 p-4 sm:p-6">
        <div className="max-w-[1400px] mx-auto">
          <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <CalendarCheck className="text-[#EFB342]" size={26} /> Daily Activity
            </h1>
            <p className="text-stone-600 mt-1 text-sm">
              Who was active each challenge day (1st–28th) and what they did. Tap a member to see their full timeline. Times are Vancouver time.
            </p>
            </div>
            <WeeklyEmailPreview />
          </header>

          {data && <DayPicker days={daysBetween(data.month_start, data.today)} selected={data.date} onSelect={setDate} />}

          {error && <p className="text-red-600 text-sm mb-4" data-testid="activity-error">{error}</p>}

          {loading && !data && (
            <div className="flex items-center justify-center py-24 text-stone-500"><Loader2 className="animate-spin mr-2" size={20} /> Loading activity…</div>
          )}

          {data && (
            <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="activity-summary">
                <StatCard icon={Users} label="Active members" value={s.active_members} testId="activity-stat-active" />
                <StatCard icon={Trophy} label="Points earned" value={s.total_points} testId="activity-stat-points" />
                <StatCard icon={Eye} label="Page visits" value={s.page_views} testId="activity-stat-pageviews" />
                <StatCard icon={LogIn} label="Logins" value={s.logins} testId="activity-stat-logins" />
                <StatCard icon={MessageCircle} label="Community" value={s.community} testId="activity-stat-community" />
              </div>
              <div className="grid md:grid-cols-2 gap-3 mb-5">
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                  <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Most visited pages</p>
                  <Chips items={s.top_pages} testId="activity-top-pages" />
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                  <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Resources opened &amp; downloaded</p>
                  <Chips items={s.top_resources} testId="activity-top-resources" />
                </div>
              </div>
              <MemberTable members={data.members} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
